/* eslint-disable max-lines */
import React from 'react';
import { Layout } from 'fronton-react';
import { PercentIcon } from '@fronton/icons-react';
import { useLocation } from '@okapi/router-react';
import { css } from 'linaria';
import { Banner, RootLayout, Typography } from 'common/components';
import {
  PDP,
  ProductDescription,
  ProductInfo,
  ProductMedia,
  ProductStickyPanel,
} from 'common/components/pdp';
import { COLLECTIONS_STORE_NAME, selectCollectionInfo, VariantAxisProduct } from 'modules/collections';
import { useEffectOnce } from 'common/hooks';
import { MODEL_JSON_STORE_NAME, selectRegionId } from 'modules/model-json';
import { useStoreon } from 'storeon/react';
import { State } from 'store/types';
import { analyticsUtils } from 'utils/analytics-utils';
import { formatToRub, removeSpecialCharacters } from 'utils/format';
import {
  getProductGAInfo,
  previewClickGAEvent,
  serviceClickGAEvent,
  serviceShowGAEvent,
  showMoreClickGAEvent,
} from 'utils';
import { RelatedService } from 'modules/related-services';
import { BELGOROD } from 'common/constants/region-codes';
import { CategoryCardSection, CollectionComposition, ServiceDescription } from './components';
import {
  KITCHEN_PRICE_PDP_CDN_PATH,
  KITCHEN_PRICE_PDP_ELEMENT_BLOCK,
  KITCHEN_PRICE_PDP_LANDING_NAME,
} from './kitchen-price-pdp-constants';
import { addToCartGAEvent, getServiceInstallationsPrice } from './kitchen-price-pdp-utils';

export const serviceInstallationWrapper = css`
  display: flex;
  align-items: center;
  padding: 12px;
  column-gap: 16px;
  background-color: #d3f0f3;
  border-radius: var(--rounding-l);
`;

export const serviceInstallationPrice = css`
  && {
    padding: 10px 20px;
    margin-left: auto;
    background-color: var(--background-primary);
    border-radius: var(--rounding-l);

    @media (--only-mobile) {
      padding: 6px 12px;
    }
  }
`;

export const serviceDisclaimerWrapper = css`
  display: flex;
  column-gap: 4px;
`;

export const KitchenPricePdp = () => {
  const [path] = useLocation();
  const { collections } = useStoreon<State>(COLLECTIONS_STORE_NAME);
  const { modelJson } = useStoreon<State>(MODEL_JSON_STORE_NAME);
  const regionId = selectRegionId(modelJson);
  const collectionInfo = selectCollectionInfo(collections);
  const activePoint = collectionInfo?.variants?.activePoint || ({} as VariantAxisProduct);

  const basketHref = `/zapis-onlayn/?department_no=15&region_id=${regionId}`;

  const productName = removeSpecialCharacters(activePoint.displayedName);

  const productGAInfo = getProductGAInfo(activePoint);

  useEffectOnce(() => {
    if (activePoint) {
      analyticsUtils.sendEvent('GA:detail', {
        elementType: 'page',
        elementBlock: KITCHEN_PRICE_PDP_ELEMENT_BLOCK, // блок, в котором расположен элемент
        productName, // название комплекта
        productId: activePoint.productId, // идентификатор комплекта
        productPrice: activePoint.price, // показанная стоимость комплекта
        productPrevPrice: null, // стоимость единицы товара до снижения цены или null
        productBelong: 'LeroyMerlin', // принадлежность товара
        komplektyId: activePoint.productId, // идентификатор комплекта
        division: 'Кухни', // отдел, к которому относится товар
        subdivision: 'Подотдел кухонь', // подотдел, к которому относится товар
        abTestPrice: null,
      });
    }
  });

  const handleAddToCartClick = () => {
    addToCartGAEvent(productGAInfo, activePoint.displayedName, 'Записаться на проектирование');
  };

  const handleAddToCartStickyPanelClick = () => {
    addToCartGAEvent(productGAInfo, 'sticky-menu', 'Записаться');
  };

  const handleOnShowMoreClick = (buttonName: string, elementName: string) => {
    showMoreClickGAEvent(buttonName, elementName, activePoint.productId, KITCHEN_PRICE_PDP_ELEMENT_BLOCK);
  };

  const handleServiceShowEvent = (service: RelatedService, position: number) => {
    serviceShowGAEvent(activePoint, service, position, KITCHEN_PRICE_PDP_ELEMENT_BLOCK);
  };

  const handleServiceClick = (service: RelatedService, position: number) => {
    serviceClickGAEvent(activePoint, service, position, KITCHEN_PRICE_PDP_ELEMENT_BLOCK);
  };

  const handlePreviewClick = (index: number) => {
    previewClickGAEvent(activePoint, activePoint.mediaPhoto[index], KITCHEN_PRICE_PDP_ELEMENT_BLOCK);
  };

  return (
    <RootLayout currentPage={path}>
      <PDP
        productMedia={
          <ProductMedia
            collectionInfo={activePoint}
            onPreviewClick={handlePreviewClick}
            landingName={KITCHEN_PRICE_PDP_LANDING_NAME}
          />
        }
        productInfo={
          <ProductInfo
            isNewKitchen
            basketHref={basketHref}
            basketHrefTarget="_blank"
            buttonLabel="Записаться на проектирование"
            disclaimer={'Без учёта бытовой техники и\u00A0столешницы'}
            infoNote={[
              'На\u00A0фото представлен образец. Чтобы узнать точную стоимость вашего проекта кухни с\u00A0установкой, запишитесь на\u00A0бесплатное проектирование.',
            ]}
            onAddToCartClick={handleAddToCartClick}
            onServiceClick={handleServiceClick}
            onServiceShowEvent={handleServiceShowEvent}
            pricePrefix="от"
            serviceDisclaimer={
              <>
                {regionId !== BELGOROD && (
                  <div className={serviceInstallationWrapper}>
                    <img
                      src={`${KITCHEN_PRICE_PDP_CDN_PATH}/services/installation-icon.png/installation-icon-1.png`}
                      alt="Сборка кухни"
                      width="40"
                      height="40"
                    />
                    <Typography variant="s" size="body_long">
                      Стоимость сборки
                    </Typography>
                    <Typography variant="s" size="body_accent" className={serviceInstallationPrice}>
                      от&nbsp;{formatToRub(getServiceInstallationsPrice(regionId))}&nbsp;₽
                    </Typography>
                  </div>
                )}
                <div className={serviceDisclaimerWrapper}>
                  <PercentIcon color="#07555E" size="m" />
                  <Typography variant="s" size="body_long" color="service-primary">
                    Вернём деньги за&nbsp;замер! Просто закажите установку у&nbsp;наших
                    специалистов&nbsp;&mdash; онлайн или в&nbsp;магазине
                  </Typography>
                </div>
              </>
            }
            servicePrefixPrice="от"
          />
        }>
        <ProductStickyPanel
          basketHref={basketHref}
          basketHrefTarget="_blank"
          buttonLabel="Записаться"
          pricePrefix="от"
          collectionInfo={activePoint}
          isCollectionAvailable
          landingName={KITCHEN_PRICE_PDP_LANDING_NAME}
          onAddToCartClick={handleAddToCartStickyPanelClick}
        />
        <Layout>
          <ServiceDescription />
          {(activePoint.description || collectionInfo.description) && (
            <ProductDescription
              description={activePoint.description || collectionInfo.description}
              onShowMoreClick={handleOnShowMoreClick}
            />
          )}
          <CollectionComposition />
          <Banner
            bgColor="#5E8A9D"
            elementBlock={KITCHEN_PRICE_PDP_ELEMENT_BLOCK}
            content={{
              media: {
                alt: 'Изображение кухни',
                srcD: `${KITCHEN_PRICE_PDP_CDN_PATH}/banner/kitchen-d.png/kitchen-d.png`,
                srcT: `${KITCHEN_PRICE_PDP_CDN_PATH}/banner/kitchen-t.png/kitchen-t.png`,
                src: `${KITCHEN_PRICE_PDP_CDN_PATH}/banner/kitchen-m.png/kitchen-m.png`,
              },
              title: 'Перейти в каталог',
              text: 'Ещё больше современных и\u00A0удобных кухонь',
            }}
            link={{
              href: '/proekt-kuhni/na-zakaz/',
              title: 'Кухни в разных стилях',
            }}
            promotion={{
              name: KITCHEN_PRICE_PDP_ELEMENT_BLOCK,
              creative: 'Каталог кухонь',
              position: '1',
            }}
          />
          <CategoryCardSection />
        </Layout>
      </PDP>
    </RootLayout>
  );
};
